import { useColorMode, useEventListener } from '@vueuse/core'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import { setDayjsLocale } from '@/libs/dayjs'
import i18n from '@/libs/i18n'
import { useFetchStore } from '@/stores/fetch'
import type { WebUser } from '@/types/Auth'

const readUserData = (): WebUser | undefined => {
    const raw = localStorage.getItem('userData')
    if (!raw) return undefined
    try {
        return JSON.parse(raw) as WebUser
    } catch {
        return undefined
    }
}

// The legacy portal kept the chosen language under `language`; read it so a returning user
// keeps theirs.
const readLocale = (): string => localStorage.getItem('locale') || localStorage.getItem('language') || 'sl'

export const useAppStore = defineStore('app', () => {
    // Auth is cookie-based (HttpOnly JWT set by the API); JS can't read the token, so we
    // persist only the WebUser payload and treat "userData present" as logged in.
    const userData     = ref<WebUser | undefined>(readUserData())
    const locale       = ref(readLocale())
    const windowWidth  = ref(window.innerWidth)
    const loaderActive = ref(0)

    ;(i18n.global.locale as unknown as { value: string }).value = locale.value
    setDayjsLocale(locale.value)

    // Wraps the SAME state @nuxt/ui's vue plugin already manages via useDark() — never a
    // second source of truth. Two modes only, 'light' | 'dark'. Without `emitAuto` the ref
    // always reads the RESOLVED mode. The topbar's ColorModeToggle binds to this.
    const colorMode = useColorMode({ storageKey: 'vueuse-color-scheme' })

    useEventListener(window, 'resize', () => { windowWidth.value = window.innerWidth })

    const isLoggedIn     = () => !!userData.value
    const isLoaderActive = () => loaderActive.value > 0
    const isMobile       = () => windowWidth.value < 1024
    const hasPermission  = (code: number) => !!userData.value?.permissions?.includes(code)

    const setLogin = (user: WebUser) => {
        userData.value = user
        localStorage.setItem('userData', JSON.stringify(user))
    }

    const setLogout = () => {
        userData.value = undefined
        localStorage.removeItem('userData')
    }

    // The theme is stored per user on the server: the login answer carries it.
    const applyUserTheme = (theme: string) => {
        colorMode.value = theme === 'dark' ? 'dark' : 'light'
    }

    const setPermissions = (permissions: number[]) => {
        if (!userData.value) return
        userData.value = { ...userData.value, permissions }
        localStorage.setItem('userData', JSON.stringify(userData.value))
    }

    // GET /ping answers 401 without a valid JWT cookie: drop a stale local login before the
    // router runs, so the guard sends the user to the login page.
    const verifySession = async () => {
        if (!userData.value) return
        try {
            // Resolved lazily (at call time) to avoid the module-init cycle with stores/fetch.ts.
            const { statusCode } = await useFetchStore().fetch('/ping').get()
            if (statusCode.value === 401) setLogout()
        } catch {
            // Network error on boot — leave state as-is; the fetch store handles later 401s.
        }
    }

    const startLoading = () => loaderActive.value++
    const stopLoading  = () => (loaderActive.value = Math.max(0, loaderActive.value - 1))

    const changeLocale = (newLocale: string) => {
        locale.value = newLocale
        localStorage.setItem('locale', newLocale)
        ;(i18n.global.locale as unknown as { value: string }).value = newLocale
        setDayjsLocale(newLocale)
    }

    return {
        userData, locale, windowWidth, loaderActive, colorMode,
        isLoggedIn, isLoaderActive, isMobile, hasPermission,
        setLogin, setLogout, applyUserTheme, setPermissions, verifySession, startLoading, stopLoading, changeLocale,
    }
})
